import DocumentViewerModal from "components/DocumentViewer";
import TeamView from "components/team_view";
import { useState } from "react";
import { formatDay } from "utils/helpers";
import { useTranslation } from "react-i18next";

interface SingleLawImplementationFollowupDetailProps {
    personImg: string;
    implementation: any;
    fileRowProps?: any;
}

export default function SingleLawImplementationFollowupDetail({ personImg, implementation, fileRowProps }: SingleLawImplementationFollowupDetailProps) {
    const { t } = useTranslation();
    const [view, setView] = useState(false);

    const teamMembers = implementation.prosecutor_assignments || [];
    const teamLeader = teamMembers.find((p: any) => p.is_team_leader || p.isTeamLeader);
    const totalMembers = teamMembers.length;

    return (
        <>
            <div className="flex flex-col bg-white rounded-lg shadow-md px-8 p-6 mb-4">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h1 className="font-semibold text-3xl text-[#212121] mb-1">
                            {t("LawImplementationPublished") || "Law Implementation Published"}
                        </h1>
                    </div>
                </div>

                <div className="flex gap-1 text-[#9D0303] font-medium text-sm mb-1">
                    <p>{formatDay(implementation.updated_at).weekday},</p>
                    <p>{formatDay(implementation.updated_at).rest}</p>
                </div>

                <div className="mt-4 mb-6">
                    {implementation.description ? (
                        <div className="space-y-4">
                            {implementation.description.split("\n").map((paragraph: string, index: number) =>
                                paragraph.trim() !== "" && (
                                    <p
                                        key={index}
                                        className="font-light text-base text-[#212121] leading-relaxed"
                                    >
                                        {paragraph}
                                    </p>
                                )
                            )}
                        </div>
                    ) : (
                        <p className="font-light text-base text-gray-500 italic">
                            {t("NoDescriptionAvailable") || "No description available"}
                        </p>
                    )}
                    
                    {implementation.enforcement_analysis_summary && (
                        <div className="space-y-4 mt-4">
                            <p className="font-semibold text-base text-[#212121]">
                                {t("EnforcementAnalysisSummary") || "Enforcement Analysis Summary"}
                            </p>
                            {implementation.enforcement_analysis_summary.split("\n").map((paragraph: string, index: number) =>
                                paragraph.trim() !== "" && (
                                    <p
                                        key={`enforcement-${index}`}
                                        className="font-light text-base text-[#212121] leading-relaxed"
                                    >
                                        {paragraph}
                                    </p>
                                )
                            )}
                        </div>
                    )}
                    
                    {implementation.audit_findings && (
                        <div className="space-y-4 mt-4">
                            <p className="font-semibold text-base text-[#212121]">
                                {t("AuditFindings") || "Audit Findings"}
                            </p>
                            {implementation.audit_findings.split("\n").map((paragraph: string, index: number) =>
                                paragraph.trim() !== "" && (
                                    <p
                                        key={`audit-${index}`}
                                        className="font-light text-base text-[#212121] leading-relaxed"
                                    >
                                        {paragraph}
                                    </p>
                                )
                            )}
                        </div>
                    )}
                </div>

                <div className="flex mt-5 mb-3 rounded-lg p-4 bg-gray-50">
                    <div className="flex w-full items-center">
                        <div className="relative mr-4">
                            <img
                                src={personImg}
                                alt={t("TeamLeaderAvatarAlt") || "Team Leader Avatar"}
                                width={48}
                                height={48}
                                className="object-cover rounded-full"
                            />
                            {totalMembers > 1 && (
                                <div className="absolute -bottom-1 -right-1 bg-gray-400 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-medium">
                                    +{totalMembers - 1}
                                </div>
                            )}
                        </div>

                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <p className="text-[#0A1D39] font-semibold text-lg">
                                    {teamLeader?.prosecutor_name || teamLeader?.name || t("Unknown") || "Unknown"}
                                </p>
                                {totalMembers > 1 && (
                                    <span className="text-gray-500 text-sm font-normal">
                                        {t("PlusMoreMembers", { count: totalMembers - 1 }) || `+ ${totalMembers - 1} more`}
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center gap-4">
                                <p className="text-[#9D0303] font-medium text-sm">
                                    {t("ImplementationTeam") || "Implementation Team"}
                                </p>
                                {totalMembers > 0 && (
                                    <div className="flex items-center text-gray-600 text-sm">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="mr-1"
                                        >
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="9" cy="7" r="4"></circle>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                        </svg>
                                        {t("MemberCount", { count: totalMembers }) || `${totalMembers} Member${totalMembers !== 1 ? "s" : ""}`}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => setView(!view)}
                        className="flex items-center gap-2 text-gray-700 hover:text-gray-400 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                        title={t("ViewTeamMembers") || "View Team Members"}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <mask
                                id="mask0_9_31087"
                                maskUnits="userSpaceOnUse"
                                x="1"
                                y="5"
                                width="22"
                                height="14"
                            >
                                <path
                                    d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                                    fill="white"
                                    stroke="white"
                                    strokeWidth="1.33333"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                                    fill="black"
                                    stroke="black"
                                    strokeWidth="1.33333"
                                    strokeLinejoin="round"
                                />
                            </mask>
                            <g mask="url(#mask0_9_31087)">
                                <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                            </g>
                        </svg>
                        <span className="font-medium text-sm">{t("ViewTeam") || "View Team"}</span>
                    </button>
                </div>
            </div>

            {(implementation.documents || []).length > 0 && (
                <div className="flex justify-between p-3 border border-gray-300 rounded-lg gap-2 mb-2 flex-col">
                    <DocumentViewerModal
                        label={t("AttachedDocuments") || "Attached Documents"}
                        documents={implementation.documents}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                    />
                </div>
            )}

            {view && (
                <TeamView
                    onClose={() => setView(false)}
                    teamMembers={teamMembers}
                />
            )}
        </>
    );
}